<template>
  <div id="pageNotificaciones">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="notification-card">
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-bell-outline</v-icon>
              <v-toolbar-title>Notificaciones</v-toolbar-title>
              <v-spacer></v-spacer>
              <!-- Badge con contador de no leídas -->
              <v-badge
                :content="unreadCount"
                :value="unreadCount > 0"
                color="error"
                overlap
                class="mr-3"
              >
                <v-btn
                  small
                  outlined
                  color="white"
                  @click="markAllAsRead"
                  :disabled="unreadCount === 0 || loadingItems"
                >
                  <v-icon left small>mdi-check-all</v-icon>
                  Marcar todo como leído
                </v-btn>
              </v-badge>
              <!-- Búsqueda -->
              <v-text-field
                v-model="filter.search"
                label="Buscar notificación..."
                prepend-inner-icon="mdi-magnify"
                hide-details
                clearable
                rounded
                single-line
                filled
                dense
                dark
                class="mr-3"
                style="max-width: 250px;"
              ></v-text-field>
              <!-- Menú de filtros -->
              <filter-menu
                title="Filtrar notificaciones"
                :current-filter="currentFilter"
                @canceled="rollbackFilter()"
                @filtered="commitFilter()"
                @showed="updateFilterList()"
              >
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="filter.type"
                      :items="notificationTypes"
                      item-text="text"
                      item-value="value"
                      label="Tipo de notificación"
                      outlined
                      clearable
                      hide-details
                      class="mb-3"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>                    </v-select>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="filter.readStatus"
                      :items="notificationReadStatus"
                      item-text="text"
                      item-value="value"
                      label="Estado de lectura"
                      outlined
                      clearable
                      hide-details
                      class="mb-3"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>                    </v-select>
                  </v-col>
                </v-row>
              </filter-menu>
              <v-btn
                icon
                class="mr-2"
                @click="refreshDataTable()"
                :disabled="loadingItems"
                title="Actualizar notificaciones"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>            </v-toolbar>
            
            <!-- Filtros rápidos bajo la barra de herramientas -->
            <v-card-text class="py-2 px-4">
              <!-- Filtros por condición -->
              <v-row v-if="hasVehicleConditionPermissions">
                <v-col cols="auto">
                  <div class="d-flex py-1">
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vn') && HAS_PERMISSION('vn')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('new')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('new') ? 'green' : 'green'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('new')"
                    >
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      VN
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vo') && HAS_PERMISSION('vo')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('used')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('used') ? 'amber' : 'amber'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('used')"
                    >
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      VO
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('taller') && HAS_PERMISSION('taller')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('taller')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('taller') ? 'indigo' : 'indigo'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('taller')"
                    >
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>
                  </div>
                </v-col>
              </v-row>

              <!-- Filtros principales -->
              <v-row class="align-center justify-center">
                <v-col cols="auto">
                  <v-chip-group v-model="quickFilter" @change="applyQuickFilter" class="py-1">
                    <v-chip filter outlined color="primary" small class="mr-1">
                      <v-icon left x-small>mdi-email</v-icon>
                      No leídas
                    </v-chip>
                    <v-chip filter outlined color="grey" small class="mr-1">
                      <v-icon left x-small>mdi-email-open</v-icon>
                      Leídas
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col cols="auto" v-if="hasActiveFilters">
                  <v-btn x-small text color="primary" @click="resetFilters" class="ml-2">
                    <v-icon left small>mdi-filter-remove</v-icon>
                    Limpiar filtros
                  </v-btn>
                </v-col>

                <v-col cols="auto" class="ml-auto">
                  <div class="d-flex align-center">                    
                    <!-- <div class="text-subtitle-1 font-weight-medium mr-4">
                      {{ filteredNotifications.length }} notificaciones encontradas
                    </div> -->
                    <v-btn-toggle
                      v-model="viewMode"
                      mandatory
                      dense
                      color="primary"
                      rounded
                    >
                      <v-btn value="cards" small>
                        <v-icon small>mdi-card-outline</v-icon>
                      </v-btn>
                      <v-btn value="table" small>
                        <v-icon small>mdi-table</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </v-col>
              </v-row>

              <!-- Filtros aplicados -->
              <v-row v-if="hasActiveFilters" class="mt-1 justify-center">
                <v-col cols="12">
                  <div class="d-flex align-center flex-wrap justify-center">
                    <span class="text-caption mr-2 grey--text">Filtros aplicados:</span>

                    <!-- Filtro de Condición -->
                    <v-chip v-if="filter.vehicle_condition && filter.vehicle_condition.includes('new')" x-small class="mr-1 mb-1" close
                      color="green" text-color="white" @click:close="applyConditionFilter('new')">
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      Nuevo
                    </v-chip>

                    <v-chip v-if="filter.vehicle_condition && filter.vehicle_condition.includes('used')" x-small class="mr-1 mb-1" close
                      color="amber" text-color="white" @click:close="applyConditionFilter('used')">
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      Usado
                    </v-chip>

                    <v-chip v-if="filter.vehicle_condition && filter.vehicle_condition.includes('taller')" x-small class="mr-1 mb-1" close
                      color="indigo" text-color="white" @click:close="applyConditionFilter('taller')">
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>

                    <v-chip v-if="filter.type" x-small class="mr-1 mb-1" close color="primary" text-color="white"
                      @click:close="filter.type = null; commitFilter()">
                      Tipo: {{ getNotificationTypeText(filter.type) }}                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>
            <!-- Vista de tarjetas -->
            <v-card-text v-if="viewMode === 'cards'" class="pa-3">
              <v-row v-if="loadingItems">
                <v-col cols="12">
                  <v-skeleton-loader
                    type="list-item-avatar-three-line"
                    class="mb-3"
                    v-for="n in 3"
                    :key="`skeleton-${n}`"
                  ></v-skeleton-loader>
                </v-col>
              </v-row>
              <v-row v-else-if="!filteredNotifications.length" class="empty-state">
                <v-col cols="12" class="text-center py-6">
                  <v-icon size="64" color="grey lighten-2" class="mb-3">mdi-bell-off-outline</v-icon>
                  <h3 class="text-h5 grey--text text--darken-1">No hay notificaciones</h3>
                  <p class="text-subtitle-1 grey--text">No se encontraron notificaciones que coincidan con los filtros seleccionados</p>
                </v-col>
              </v-row>
              <template v-else>                <v-card
                  v-for="item in filteredNotifications"
                  :key="item.id"
                  outlined
                  :class="['notification-card-item mb-3', {'unread-notification': !item.read_at}]"
                  @click="viewNotificationDetails(item)"
                >
                  <v-card-text class="pa-3">
                    <v-row no-gutters>
                      <v-col cols="auto" class="mr-3">
                        <v-avatar
                          size="48"
                          :color="getNotificationTypeColor(item.type)"
                          class="white--text elevation-1"
                        >
                          <v-icon>{{ getNotificationTypeIcon(item.type) }}</v-icon>
                        </v-avatar>
                      </v-col>
                      <v-col>
                        <div class="d-flex justify-space-between align-center">
                          <div>
                            <div class="subtitle-1 font-weight-medium mb-1">
                              {{ getNotificationTitle(item) }}
                              <v-chip
                                x-small
                                label
                                class="ml-2"
                                :color="item.read_at ? 'grey lighten-1' : 'primary'"
                                text-color="white"
                              >
                                {{ item.read_at ? 'Leído' : 'Nuevo' }}
                              </v-chip>
                            </div>
                            <div class="caption grey--text text--darken-1">
                              {{ formatDate(item.created_at) }}
                            </div>
                          </div>
                          <div>
                            <v-btn
                              v-if="CURRENT_USER.roles.includes('logistics')" 
                              icon
                              small
                              :color="item.read_at ? 'grey' : 'primary'"
                              @click.stop="toggleReadStatus(item)"
                              :title="item.read_at ? 'Marcar como no leído' : 'Marcar como leído'"
                            >
                              <v-icon>{{ item.read_at ? 'mdi-email-open-outline' : 'mdi-email-outline' }}</v-icon>
                            </v-btn>
                            <!-- <v-btn
                              icon
                              small
                              color="red"
                              @click.stop="deleteNotificacion(item)"
                              title="Eliminar notificación"
                            >
                              <v-icon>mdi-delete-outline</v-icon>
                            </v-btn> -->
                          </div>
                        </div>
                        <div class="body-2 mt-2">
                          {{ getNotificationContent(item) }}
                        </div>
                        <!-- Chips para vehículos (vehicle, damage_movement, damage_first_reception_used, damage_first_reception_new) -->
                        <v-chip-group v-if="(item.type === 'vehicle' || item.type === 'damage_movement' || item.type === 'damage_first_reception_used' || item.type === 'damage_first_reception_new') && item.vehicle" class="mt-2">
                          <v-chip
                            small
                            outlined
                            class="mr-1"
                          >
                            <v-icon left small>mdi-car</v-icon>
                            {{ item.vehicle.brand }} {{ item.vehicle.model }}
                          </v-chip>
                          <v-chip
                            small
                            outlined
                            class="mr-1"
                          >
                            <v-icon left small>mdi-numeric</v-icon>
                            {{ item.vehicle.plate_number }}
                          </v-chip>
                          <v-chip
                            small
                            outlined
                            :color="getStatusColor(item.vehicle.status)"
                          >
                            <v-icon left small>mdi-information-outline</v-icon>
                            {{ getStatusText(item.vehicle.status) }}
                          </v-chip>
                        </v-chip-group>
                        <!-- Chips para equipamiento faltante -->
                        <v-chip-group v-else-if="item.type && item.type.startsWith('equipment_missing_') && item.vehicle" class="mt-2">
                          <v-chip
                            small
                            outlined
                            class="mr-1"
                          >
                            <v-icon left small>mdi-car</v-icon>
                            {{ item.vehicle.brand }} {{ item.vehicle.model }}
                          </v-chip>
                          <v-chip
                            small
                            outlined
                            class="mr-1"
                          >
                            <v-icon left small>mdi-numeric</v-icon>
                            {{ item.vehicle.plate_number }}
                          </v-chip>
                          <v-chip
                            small
                            outlined
                            color="warning"
                          >
                            <v-icon left small>mdi-alert</v-icon>
                            Falta equipamiento
                          </v-chip>
                        </v-chip-group>
                        <!-- Chips para movimientos -->
                        <v-chip-group v-else-if="(item.type === 'movement' || item.type === 'movement_datetime_changed') && item.movement" class="mt-2">
                          <v-chip
                            small
                            outlined
                            class="mr-1"
                          >
                            <v-icon left small>mdi-calendar</v-icon>
                            {{ formatDate(item.movement.created_at) }}
                          </v-chip>
                          <v-chip
                            small
                            outlined
                            :color="getMovementTypeColor(item.movement.type)"
                          >
                            <v-icon left small>mdi-{{ item.movement.type === 'entry' ? 'arrow-down-circle' : 'arrow-up-circle' }}</v-icon>
                            {{ item.movement.type === 'entry' ? 'Entrada' : 'Salida' }}
                          </v-chip>
                        </v-chip-group>
                      </v-col>
                    </v-row>
                  </v-card-text>                </v-card>
                <!-- Paginación -->
                <v-row v-if="filteredNotifications.length > 0">
                  <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                    <div class="text-center d-flex align-center mr-4">
                      <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                      <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                        hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                    </div>

                    <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                      :total-visible="7" circle @input="onPageChange"></v-pagination>

                    <span class="text-body-2 grey--text text--darken-1 ml-4">
                      {{ paginationText }}
                    </span>
                  </v-col>
                </v-row>
              </template>
            </v-card-text>
            <!-- Vista de tabla -->
            <v-card-text v-else class="pa-0">              
              <v-data-table
                :headers="headers"
                :items="filteredNotifications"
                :options.sync="pagination"
                :server-items-length="totalItems"
                :loading="loadingItems"
                :footer-props="footer"
                :key="tableId"
                class="elevation-0"
                multi-sort
                @click:row="viewNotificationDetails"
              >
                <!-- Tipo de notificación -->
                <template v-slot:item.type="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" :color="getNotificationTypeColor(item.type) + ' lighten-4'" class="mr-2">
                      <v-icon :color="getNotificationTypeColor(item.type)" small>
                        {{ getNotificationTypeIcon(item.type) }}
                      </v-icon>
                    </v-avatar>
                    {{ getNotificationTypeText(item.type) }}
                  </div>
                </template>
                <!-- Fecha formateada -->
                <template v-slot:item.created_at="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>
                <!-- Información del vehículo o movimiento -->
                <template v-slot:item.details="{ item }">
                  <!-- VEHICLE -->
                  <template v-if="item.type === 'vehicle' && item.vehicle">
                  <div>
                    <strong>{{ item.vehicle.brand }} {{ item.vehicle.model }}</strong>
                    <v-chip x-small class="ml-1" :color="getStatusColor(item.vehicle.status)" dark>
                    {{ getStatusText(item.vehicle.status) }}
                    </v-chip>
                  </div>
                  <div class="caption">Matrícula: {{ item.vehicle.plate_number }}</div>
                  </template>
                  <!-- MOVEMENT -->
                  <template v-else-if="(item.type === 'movement' || item.type === 'movement_datetime_changed') && item.movement">
                  <div>
                    <strong>{{ item.type === 'movement_datetime_changed' ? (item.title || 'Fecha de movimiento modificada') : (item.movement.type === 'entry' ? 'Entrada' : 'Salida') }}</strong>
                  </div>
                  <div class="caption">{{ formatDate(item.movement.created_at) }}</div>
                  </template>
                  <!-- DAMAGE MOVEMENT -->
                  <template v-else-if="item.type === 'damage_movement' && item.movement">
                  <div>
                    <strong>Vehículo {{ item.vehicle.brand }} {{ item.vehicle.model }} dañado</strong>
                  </div>
                  <div class="caption">{{ formatDate(item.movement.created_at) }}</div>
                  </template>
                  <!-- DAMAGE FIRST RECEPTION USED -->
                  <template v-else-if="item.type === 'damage_first_reception_used' && item.movement">
                  <div>
                    <strong>Vehículo {{ item.vehicle.brand }} {{ item.vehicle.model }} usado dañado 1ra recepción</strong>
                  </div>
                  <div class="caption">{{ formatDate(item.movement.created_at) }}</div>
                  </template>
                  <!-- DAMAGE FIRST RECEPTION NEW -->
                  <template v-else-if="item.type === 'damage_first_reception_new' && item.movement">
                  <div>
                    <strong>Vehículo {{ item.vehicle.brand }} {{ item.vehicle.model }} nuevo dañado 1ra recepción</strong>
                  </div>
                  <div class="caption">{{ formatDate(item.movement.created_at) }}</div>
                  </template>
                  <!-- EQUIPMENT MISSING -->
                  <template v-else-if="item.type && item.type.startsWith('equipment_missing_') && item.vehicle">
                  <div>
                    <strong>Falta equipamiento: 
                    {{
                      ({
                      baliza_v16: 'Baliza V16',
                      chalecos: 'Chaleco reflectante',
                      triangulo: 'Triángulo de emergencia',
                      documentacion: 'Documentación',
                      cable_carga: 'Cable de carga'
                      })[item.type.replace('equipment_missing_', '')] || item.type.replace('equipment_missing_', '')
                    }}
                    </strong>
                  </div>
                  <div class="caption">
                    Matrícula: {{ item.vehicle.plate_number }}
                  </div>
                  </template>
                  <!-- DEFAULT -->
                  <template v-else>
                  <div class="caption grey--text">Sin detalles</div>
                  </template>
                </template>
                <!-- Estado de lectura -->
                <template v-slot:item.read_at="{ item }">
                  <v-chip small :color="item.read_at ? 'grey lighten-1' : 'primary'" text-color="white">
                    {{ item.read_at ? 'Leído' : 'Nuevo' }}
                  </v-chip>
                </template>
                <!-- Acciones -->
                <template v-slot:item.action="{ item }">
                  <div class="d-inline-flex action-column">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          small
                          :color="item.read_at ? 'grey' : 'primary'"
                          @click.stop="toggleReadStatus(item)"
                          v-on="on"
                        >
                          <v-icon size="20">{{ item.read_at ? 'mdi-email-open-outline' : 'mdi-email-outline' }}</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ item.read_at ? 'Marcar como no leído' : 'Marcar como leído' }}</span>
                    </v-tooltip>
                    <!-- <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          small
                          color="info"
                          @click.stop="viewNotificationDetails(item)"
                          v-on="on"
                        >
                          <v-icon size="20">mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver detalles</span>
                    </v-tooltip> -->
                    <!-- <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          small
                          color="red"
                          @click.stop="deleteNotificacion(item)"
                          v-on="on"
                          v-has-permission="{
                            roles: ['admin', 'logistics']
                          }"
                        >
                          <v-icon size="20">mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Eliminar</span>
                    </v-tooltip> -->
                  </div>
                </template>
                
                <!-- Sin resultados -->
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <v-icon size="64" color="grey lighten-2" class="mb-3">mdi-bell-off-outline</v-icon>
                    <h3 class="text-h5 grey--text text--darken-1 mb-2">No hay notificaciones</h3>
                    <p class="subtitle-1 grey--text mb-4">
                      No se encontraron notificaciones que coincidan con los filtros seleccionados
                    </p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <!-- Diálogo para ver detalles de la notificación -->
    <v-dialog v-model="detailDialog.visible" max-width="500">
      <v-card v-if="detailDialog.item">
        <v-card-title :class="['white--text', getNotificationTypeColor(detailDialog.item.type)]">
          <v-icon left color="white">{{ getNotificationTypeIcon(detailDialog.item.type) }}</v-icon>
          {{ getNotificationTitle(detailDialog.item) }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-list dense class="pa-0">
            <v-list-item two-line>
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Fecha</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(detailDialog.item.created_at) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="(detailDialog.item.type === 'vehicle' || detailDialog.item.type === 'damage_movement') && detailDialog.item.vehicle">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Vehículo</v-list-item-subtitle>
                <v-list-item-title>
                  {{ detailDialog.item.vehicle.brand }} {{ detailDialog.item.vehicle.model }} ({{ detailDialog.item.vehicle.version }})
                  <v-chip
                    v-if="detailDialog.item.type === 'damage_movement'"
                    small
                    color="deep-orange"
                    class="ml-2 white--text"
                    label
                  >
                    <v-icon left small>mdi-car-brake-alert</v-icon>
                    Vehículo dañado
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="(detailDialog.item.type === 'vehicle' || detailDialog.item.type === 'damage_movement') && detailDialog.item.vehicle">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Matrícula</v-list-item-subtitle>
                <v-list-item-title>{{ detailDialog.item.vehicle.plate_number }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="(detailDialog.item.type === 'vehicle' || detailDialog.item.type === 'damage_movement') && detailDialog.item.vehicle">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Estado</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip small :color="getStatusColor(detailDialog.item.vehicle.status)" dark>
                    {{ getStatusText(detailDialog.item.vehicle.status) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item two-line v-if="detailDialog.item.user">
              <v-list-item-content>
                <v-list-item-subtitle class="caption">Usuario</v-list-item-subtitle>
                <v-list-item-title>
                  {{ detailDialog.item.user.name }} {{ detailDialog.item.user.lastname }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-1"
            @click="detailDialog.visible = false"
          >
            Cerrar
          </v-btn>
          <v-btn
            text
            color="primary"
            @click="goToRelatedItem(detailDialog.item)"
          >
            Ver detalle completo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import FilterMenu from '../../components/FilterMenu'
import NotificacionService from '../../services/NotificacionService'

export default {
  components: {
    FilterMenu
  },

  data: () => ({
    // Vista y carga
    loadingItems: false,
    totalItems: 0,
    tableId: 0,
    unreadCount: 0,
    quickFilter: null, // Filtro rápido: 0 = No leídas (por defecto), 1 = Leídas
    conditionFilter: null,
    // Diálogo de detalles
    detailDialog: {
      visible: false,
      item: null
    },
    // Tabla
    headers: [
      { text: 'Tipo', value: 'type', sortable: true },
      { text: 'Fecha', value: 'created_at', sortable: true },
      { text: 'Detalles', value: 'details', sortable: false },
      { text: 'Estado', value: 'read_at', sortable: true },
      { text: 'Acciones', value: 'action', sortable: false, align: 'center' }
    ],
    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      itemsPerPageText: 'Notificaciones por página:',
      pageText: '{0}-{1} de {2}'
    },
    // Datos de notificaciones
    notificacion: [],
    // Filtros
    filter: {
      search: null,
      type: null,
      readStatus: null,
      vehicle_condition: []
    },
    currentFilter: {
      search: null,
      type: null,
      readStatus: null,
      vehicle_condition: []
    },
    error: false,
    // Opciones para filtros
    notificationTypes: [
      { text: 'Vehículos', value: 'vehicle', icon: 'mdi-car', color: 'blue' },
      { text: 'Movimientos', value: 'movement', icon: 'mdi-truck-delivery', color: 'green' },
      { text: 'Daños en movimiento', value: 'damage_movement', icon: 'mdi-car-brake-alert', color: 'deep-orange' }
    ],
    notificationReadStatus: [
      { text: 'No leídas', value: 0, icon: 'mdi-email', color: 'primary' },
      { text: 'Leídas', value: 1, icon: 'mdi-email-open', color: 'grey' }
    ],
    manualViewMode: null
  }),

  methods: {
    // Carga de datos
    loadData () {
      // No cargar notificaciones si el usuario es admin
      if (this.CURRENT_USER && this.CURRENT_USER.roles && this.CURRENT_USER.roles.includes('admin')) {
        console.log('Usuario admin detectado - no se cargan notificaciones')
        this.loadingItems = false
        this.totalItems = 0
        this.unreadCount = 0
        return
      }
      
      this.loadingItems = true
      let currentFilter = { ...this.filter, user_id: this.CURRENT_USER.id }
      this.$store.dispatch('LOAD_NOTIFICACIONES', currentFilter).then(result => {
        this.totalItems = result
        this.calculateUnreadCount()
        this.loadingItems = false
      })
    },
    debouncedLoadData: _.debounce(function () {
      this.loadData()
    }, 1000),
    refreshDataTable () {
      this.tableId++
      this.loadData()
    },
    async calculateUnreadCount () {
      const unread = await NotificacionService.unreadCount()
      console.log(unread.data.data.unread_count)
      
      this.unreadCount = unread.data.data.unread_count || 0
    },
    // Filtros
    commitFilter () {
      for (let [key, value] of Object.entries(this.filter)) {
        this.currentFilter[key] = value
      }
      this.loadData()
    },
    rollbackFilter () {
      for (let [key, value] of Object.entries(this.currentFilter)) {
        this.filter[key] = value
      }
    },
    updateFilterList () {
      // Método para actualizar listas de filtros si es necesario
    },
    // Acciones de notificaciones
    deleteNotificacion (item) {
      this.$confirm('¿Está seguro que desea eliminar esta notificación?').then(result => {
        if (result) {
          this.loadingItems = true
          this.$store.dispatch('DELETE_NOTIFICACIONES', item).then(() => {
            this.refreshDataTable()
            this.$toast.success('Notificación eliminada con éxito')
            this.loadingItems = false
          })
        }
      })
    },
    toggleReadStatus (item) {
      this.loadingItems = true
      const action = item.read_at ? 'UNREAD_NOTIFICATION' : 'READ_NOTIFICATION'
      this.$store.dispatch(action, item).then(() => {
        item.read_at = action === 'READ_NOTIFICATION' ? Date.now() : null
        this.calculateUnreadCount()
        this.loadingItems = false
        // Recargar la lista para mantenerla actualizada
        this.loadData()
      }).catch((error) => {
        console.error('Error en toggleReadStatus:', error)
        this.loadingItems = false
      })
    },
    markAllAsRead () {
      this.$confirm('¿Marcar todas las notificaciones como leídas?').then(async result => {
        if (result) {
          this.loadingItems = true
          await NotificacionService.readAll()
          this.calculateUnreadCount()
          this.loadingItems = false
          if (!this.error) {
            this.$toast.success('Todas las notificaciones marcadas como leídas')
            this.refreshDataTable()
          } else {
            this.$toast.error('Error al marcar todas las notificaciones como leídas')
          }
          this.error = false
        }
      })
    },
    viewNotificationDetails(item) {
      // Marcar como leída si no lo está
      if (!item.read_at && this.CURRENT_USER.roles.includes('logistics')) {
        this.$store.dispatch('READ_NOTIFICATION', item).then(() => {
          item.read_at = Date.now()
          this.calculateUnreadCount()
          // Recargar la lista para mantenerla actualizada
          this.loadData()
        })
      }
      // Si es una notificación de tipo movimiento, ir directamente a la página de detalles
      if ((item.type === 'movement' || item.type === 'movement_datetime_changed') && item.movement && item.movement.id) {
        this.$router.push({ name: 'MovimientoDetail', params: { movimiento_id: item.movement.id } })
        return
      }
      // Si es una notificación de daño en movimiento, daños en 1ra recepción, o falta de equipamiento, ir a detalle del movimiento
      if (
        (item.type === 'damage_movement' || item.type === 'damage_first_reception_used' || item.type === 'damage_first_reception_new' || (item.type && item.type.startsWith('equipment_missing_'))) && item.movement && item.movement.id
      ) {
        this.$router.push({ name: 'MovimientoDetail', params: { movimiento_id: item.movement.id } })
        return
      }
      this.detailDialog.item = item
      this.detailDialog.visible = true
    },
    goToRelatedItem(item) {
      this.detailDialog.visible = false
      if (item.type === 'vehicle' && item.vehicle) {
        this.$router.push({ name: 'VehiculoDetail', params: { id: item.vehicle.id } })
      } else if ((item.type === 'movement' || item.type === 'movement_datetime_changed') && item.movement) {
        this.$router.push({ name: 'MovimientoDetail', params: { movimiento_id: item.movement.id } })
      } else if (
        (item.type === 'damage_movement' || item.type === 'damage_first_reception_used' || item.type === 'damage_first_reception_new') && item.movement && item.movement.id
      ) {
        this.$router.push({ name: 'MovimientoDetail', params: { movimiento_id: item.movement.id } })
      }
    },
    // Formato y presentación
    formatDate (timestamp, short) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      if (short) {
        return new Intl.DateTimeFormat('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        }).format(date)
      }
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    getNotificationTypeIcon (type) {
      if (type && type.startsWith('equipment_missing_')) return 'mdi-car'
      const icons = {
        'vehicle': 'mdi-car',
        'movement': 'mdi-truck-delivery',
        'movement_datetime_changed': 'mdi-truck-delivery',
        'damage_movement': 'mdi-car',
        'damage_first_reception_used': 'mdi-car',
        'damage_first_reception_new': 'mdi-car'
      }
      return icons[type] || 'mdi-bell'
    },
    getNotificationTypeColor (type) {
  if (type && type.startsWith('equipment_missing_')) return 'blue'
      const colors = {
        'vehicle': 'blue',
        'movement': 'green',
        'movement_datetime_changed': 'green',
        'damage_movement': 'blue',
        'damage_first_reception_used': 'blue',
        'damage_first_reception_new': 'blue'
      }
      return colors[type] || 'grey'
    },
    getNotificationTypeText (type) {
      if (type && type.startsWith('equipment_missing_')) return 'Falta equipamiento'
      const texts = {
        'vehicle': 'Vehículo',
        'movement': 'Movimiento',
        'movement_datetime_changed': 'Movimiento',
        'damage_movement': 'Vehículo',
        'damage_first_reception_used': 'Vehículo',
        'damage_first_reception_new': 'Vehículo' // igual que vehicle
      }
      return texts[type] || 'Desconocido'
    },
    getStatusColor (status) {
      const colors = {
        'pending': 'orange',
        'created': 'success',
        'in_transit': 'blue',
        'delivered': 'green',
        'cancelled': 'red'
      }
      return colors[status] || 'grey'
    },
    getStatusText (status) {
      const texts = {
        'pending': 'Pendiente',
        'created': 'Creado',
        'in_transit': 'En tránsito',
        'delivered': 'Entregado',
        'cancelled': 'Cancelado'
      }
      return texts[status] || 'Desconocido'
    },
    getMovementTypeColor (type) {
      return type === 'entry' ? 'green' : 'blue'
    },
    getNotificationTitle (item) {
      if (item.type && item.type.startsWith('equipment_missing_')) {
        const equip = item.type.replace('equipment_missing_', '')
        const equipMap = {
          'baliza_v16': 'Baliza V16',
          'chalecos': 'Chaleco reflectante',
          'triangulo': 'Triángulo de emergencia',
          'documentacion': 'Documentación',
          'cable_carga': 'Cable de carga'
        }
        const equipLabel = equipMap[equip] || equip
        return `Falta equipamiento: ${equipLabel} (${item.vehicle?.plate_number || ''})`
      }
      if (item.type === 'vehicle') {
        return item.vehicle ? `Vehículo: ${item.vehicle.brand} ${item.vehicle.model}` : 'Notificación de vehículo'
      } else if (item.type === 'movement') {
        return item.movement ? `Movimiento: ${item.movement.type === 'entry' ? 'Entrada' : 'Salida'}` : 'Notificación de movimiento'
      } else if (item.type === 'movement_datetime_changed') {
        if (item.title) {
          return item.title
        }
        const vehicleInfo = item.vehicle?.plate_number || (item.vehicle ? `${item.vehicle.brand} ${item.vehicle.model}` : '')
        return vehicleInfo ? `Fecha de movimiento modificada - Matrícula: ${vehicleInfo}` : 'Fecha de movimiento modificada'
      } else if (item.type === 'damage_movement') {
        return item.vehicle ? `Daño en movimiento: ${item.vehicle.brand} ${item.vehicle.model}` : 'Notificación de daño en movimiento'
      }
      return 'Notificación'
    },
    getNotificationContent (item) {
      if (item.type && item.type.startsWith('equipment_missing_')) {
        const equip = item.type.replace('equipment_missing_', '')
        const equipMap = {
          'baliza_v16': 'Baliza V16',
          'chalecos': 'Chaleco reflectante',
          'triangulo': 'Triángulo de emergencia',
          'documentacion': 'Documentación',
          'cable_carga': 'Cable de carga'
        }
        const equipLabel = equipMap[equip] || equip
        return `El vehículo con matrícula ${item.vehicle?.plate_number || ''} no tiene asignado el equipamiento: ${equipLabel}.`
      }
      if (item.type === 'vehicle') {
        return `Se ha ${item.vehicle?.status === 'created' ? 'creado' : 'actualizado'} el vehículo ${item.vehicle?.brand} ${item.vehicle?.model} con matrícula ${item.vehicle?.plate_number}.`
      } else if (item.type === 'movement') {
        return `Se ha registrado un movimiento de ${item.movement?.type === 'entry' ? 'entrada' : 'salida'}.`
      } else if (item.type === 'movement_datetime_changed') {
        const vehicleInfo = item.vehicle?.plate_number || (item.vehicle ? `${item.vehicle.brand} ${item.vehicle.model}` : 'un vehículo')
        return `Se ha modificado la fecha del movimiento del vehículo ${vehicleInfo}.`
      } else if (item.type === 'damage_movement') {
        return `Se ha reportado un daño en el vehículo ${item.vehicle?.brand} ${item.vehicle?.model} (${item.vehicle?.plate_number}).`
      } else if (item.type === 'damage_first_reception_used') {
        return `Vehículo usado ${item.vehicle?.brand} ${item.vehicle?.model} dañado en 1ra recepción (${item.vehicle?.plate_number}).`
      } else if (item.type === 'damage_first_reception_new') {
        return `Vehículo nuevo ${item.vehicle?.brand} ${item.vehicle?.model} dañado en 1ra recepción (${item.vehicle?.plate_number}).`
      }
      return 'Sin detalles adicionales.'
    },
    // Pagination methods
    onPageChange (page) {
      // Actualizar la página en la paginación
      this.pagination.page = page
      // Forzar la carga de datos inmediatamente después del cambio de página
      this.loadData()
    },
    onItemsPerPageChange (perPage) {
      // Actualizar items por página en la paginación
      this.pagination.itemsPerPage = perPage
      // Asegurarse de volver a la primera página al cambiar elementos por página
      this.pagination.page = 1
      // Forzar la carga de datos
      this.loadData()
    },
    // Métodos para filtros rápidos
    applyQuickFilter (index) {
      // No necesitamos hacer nada especial, solo cambiar el quickFilter
      // La computed property filteredNotifications se encarga del resto
    },
    applyConditionFilter(condition) {
      // Si el array no está inicializado, inicializarlo
      if (!Array.isArray(this.filter.vehicle_condition)) {
        this.filter.vehicle_condition = []
      }
      
      // Si la condición ya está en el array, quitarla
      const index = this.filter.vehicle_condition.indexOf(condition)
      if (index > -1) {
        this.filter.vehicle_condition.splice(index, 1)
      } else {
        // Si no está, agregarla
        this.filter.vehicle_condition.push(condition)
      }
      
      this.commitFilter()
    },
    resetFilters () {
      this.filter.search = null
      this.filter.type = null
      this.filter.vehicle_condition = []
      this.quickFilter = 0 // Resetear a "No leídas"
      this.conditionFilter = null
      this.commitFilter()
    }
  },

  computed: {
    ...mapGetters(['NOTIFICACIONES', 'CURRENT_USER', 'HAS_PERMISSION', 'DEALER_HAS_TYPE']),
    
    viewMode: {
      get() {
        // Si el usuario cambió manualmente la vista, usar esa
        if (this.manualViewMode) {
          return this.manualViewMode
        }
        // Detectar dispositivo y retornar vista por defecto
        if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.mdAndUp) {
          return 'table'
        }
        return 'cards'
      },
      set(value) {
        // Permitir cambio manual de vista
        this.manualViewMode = value
      }
    },
    
    // Paginación
    pagination: {
      get: function () {
        return this.$store.getters.NOTIFICACIONES_PAG
      },
      set: function (value) {
        this.$store.commit('SET_NOTIFICACIONES_PAG', value)
      }
    },
    searchTerm () {
      return this.filter.search
    },
    totalPages () {
      return Math.ceil(this.totalItems / this.pagination.itemsPerPage)
    },
    paginationText () {
      const totalFiltered = this.filteredNotifications.length
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, totalFiltered)
      return `${start}-${end} de ${totalFiltered}`
    },
    hasUnreadNotifications () {
      return this.unreadCount > 0
    },
    hasActiveFilters () {
      return this.filter.type !== null || 
             (Array.isArray(this.filter.vehicle_condition) && this.filter.vehicle_condition.length > 0)
    },
    hasVehicleConditionPermissions() {
      // Mostrar filtros si el usuario tiene al menos 1 tipo de vehículo activo en el dealer
      const types = ['vn', 'vo', 'taller']
      const count = types.reduce((acc, type) => {
        // Primero verificar si el dealer tiene el tipo, luego verificar permisos
        return acc + (this.DEALER_HAS_TYPE(type) && this.HAS_PERMISSION(type) ? 1 : 0)
      }, 0)
      return count >= 1
    },
    // Filtro local para notificaciones leídas/no leídas
    filteredNotifications () {
      let filtered = this.notificacion
      if (this.filter.type) {
        if (this.filter.type === 'vehicle') {
          filtered = filtered.filter(item => item.type === 'vehicle' || item.type === 'damage_movement')
        } else {
          filtered = filtered.filter(item => item.type === this.filter.type)
        }
      }
      // Filtrar por condición del vehículo
      if (this.filter.vehicle_condition && Array.isArray(this.filter.vehicle_condition) && this.filter.vehicle_condition.length > 0) {
        filtered = filtered.filter(item => {
          return item.vehicle && this.filter.vehicle_condition.includes(item.vehicle.condition)
        })
      }
      if (this.quickFilter === 0) {
        filtered = filtered.filter(item => !item.read_at)
      } else if (this.quickFilter === 1) {
        filtered = filtered.filter(item => item.read_at)
      }
      if (this.filter.search) {
        const searchTerm = this.filter.search.toLowerCase()
        filtered = filtered.filter(item => {
          return (
            item.vehicle?.brand.toLowerCase().includes(searchTerm) ||
            item.vehicle?.model.toLowerCase().includes(searchTerm) ||
            item.vehicle?.plate_number.toLowerCase().includes(searchTerm) ||
            (item.movement && item.movement.type === 'entry' ? 'entrada' : 'salida').includes(searchTerm) ||
            (item.movement && this.formatDate(item.movement.created_at, true).includes(searchTerm))
          )
        })
      }

      return filtered
    }
  },
  watch: {
    NOTIFICACIONES (newVal) {
      this.notificacion = newVal
      this.calculateUnreadCount()
    },
    pagination: {
      handler: _.debounce(function (newVal, oldVal) {
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        if (relevantPropsChanged && !this.loadingItems) {
          this.loadData()
        }
      }, 200), // pequeño delay
      deep: true
    },
    searchTerm () {
      this.debouncedLoadData()
    },
    manualViewMode (newVal, oldVal) {
      // Incrementar tableId cuando cambiamos a vista de tabla para forzar re-renderizado
      if (newVal === 'table' && oldVal === 'cards') {
        this.$nextTick(() => {
          this.tableId++
          setTimeout(() => {}, 100)
        })
      }
    }
  },

  async created () {
    // No cargar notificaciones si el usuario es admin
    if (this.CURRENT_USER && this.CURRENT_USER.roles && this.CURRENT_USER.roles.includes('admin')) {
      console.log('Usuario admin detectado - no se cargan notificaciones')
      this.loadingItems = false
      return
    }
    
    this.loadData()
  }
}
</script>

<style scoped>
.notification-card {
  border-radius: 8px;
  overflow: hidden;
}

.notification-card-item {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  cursor: pointer;
  border-radius: 8px;
}

.notification-card-item:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.unread-notification {
  border-left: 4px solid var(--v-primary-base) !important;
  background-color: #f5f9ff;
}

.action-column button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-column button:hover {
  opacity: 1;
}

.search-field >>> .v-input__slot {
  min-height: 36px !important;
}

.empty-state {
  min-height: 200px;
}

/* Animación para nuevas notificaciones */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.new-notification {
  animation: highlight 2s ease-in-out;
}
</style>
